import { Link, NavLink, useLocation } from "react-router-dom";
import { forwardRef, useCallback } from "react";

/**
 * Same-path click behavior — React Router treats a click on a link to the
 * URL you're already viewing as a no-op (no pathname change, no state
 * change, ScrollToTop never fires). Per owner directive (2026-05-24): a
 * click on a nav link that points to the page you're already on should
 * scroll you back to the top, exactly like a fresh navigation would.
 *
 * Comparison strips query string + hash so `?sort=longest` and
 * `#section-x` don't accidentally short-circuit a real navigation.
 *
 * We use a smooth scroll so the visitor sees motion (visual "refresh"
 * cue) rather than a jarring jump. Falls back to instant scroll for
 * visitors with `prefers-reduced-motion: reduce`.
 */
function handleSamePathClick(event, to, currentPath, userOnClick) {
  if (userOnClick) userOnClick(event);
  if (event.defaultPrevented) return;
  // Only handle plain left-clicks without modifiers — preserve
  // open-in-new-tab, middle-click, ctrl-click, etc.
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const target = typeof to === "string" ? to : to?.pathname || "";
  // If the target has a hash, always let the router handle it so
  // hash-anchor scroll works — even when the base path matches (e.g.
  // clicking a share-deep-link back to the same /testimonials page).
  if (target.includes("#")) return;
  const targetPath = target.split("?")[0];
  if (!targetPath || targetPath !== currentPath) return;

  event.preventDefault();
  if (typeof window === "undefined") return;
  const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  window.scrollTo({ top: 0, left: 0, behavior: prefersReducedMotion ? "auto" : "smooth" });
}

/**
 * SmartLink — drop-in replacement for react-router-dom's <Link> that
 * scrolls to top when clicked on the page it points at.
 */
export const SmartLink = forwardRef(function SmartLink({ to, onClick, ...rest }, ref) {
  const { pathname } = useLocation();
  const handleClick = useCallback(
    (event) => handleSamePathClick(event, to, pathname, onClick),
    [to, pathname, onClick]
  );
  return <Link ref={ref} to={to} onClick={handleClick} {...rest} />;
});

/**
 * SmartNavLink — drop-in replacement for react-router-dom's <NavLink>
 * with the same same-path scroll-to-top behavior. Preserves NavLink's
 * isActive/isPending render-prop API.
 */
export const SmartNavLink = forwardRef(function SmartNavLink({ to, onClick, ...rest }, ref) {
  const { pathname } = useLocation();
  const handleClick = useCallback(
    (event) => handleSamePathClick(event, to, pathname, onClick),
    [to, pathname, onClick]
  );
  return <NavLink ref={ref} to={to} onClick={handleClick} {...rest} />;
});
